// Ảnh khuôn mặt lúc chấm công (vào/ra) dạng ô nhỏ; bấm để xem lớn và chuyển qua lại giữa 2 ảnh.
// Dữ liệu: checkinImageUrl / checkoutImageUrl của GET /attendance/list (URL đầy đủ, có thể null
// với bản ghi chấm tay / quản trị tạo).

import { CSSProperties, cloneElement, useEffect, useState } from 'react';
import { Image, Tooltip } from 'antd';
import { EyeOutlined, UserOutlined } from '@ant-design/icons';
import moment from 'moment';
import { colors } from 'theme/tokens';

export interface AttendancePhotoSource {
  checkinImageUrl?: string | null;
  checkoutImageUrl?: string | null;
  checkinTime?: string | null;
  checkoutTime?: string | null;
}

type PhotoKind = 'in' | 'out';

interface PhotoSlot {
  kind: PhotoKind;
  label: string;
  url: string;
  time?: string | null;
}

// Ảnh hỏng/không tải được → ô xám có biểu tượng ảnh gạch chéo (SVG nhúng, không gọi mạng).
const FALLBACK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" fill="${colors.surfaceMuted}"/><g fill="none" stroke="${colors.textMuted}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><rect x="26" y="30" width="44" height="36" rx="5"/><circle cx="39" cy="42" r="4"/><path d="M29 63l13-12 9 8 7-6 11 10"/><path d="M22 22l52 52"/></g></svg>`;
export const PHOTO_FALLBACK = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  FALLBACK_SVG
)}`;

const KIND_TEXT: Record<PhotoKind, { clock: string; missingTime: string }> = {
  in: { clock: 'Giờ vào', missingTime: 'Chưa check-in' },
  out: { clock: 'Giờ ra', missingTime: 'Chưa check-out' },
};

const clockOf = (value?: string | null) => (value ? moment(value).format('HH:mm') : '');

function captionOf(slot: PhotoSlot, personName?: string) {
  const clock = clockOf(slot.time);
  const line = clock
    ? `${KIND_TEXT[slot.kind].clock} ${clock}`
    : `Ảnh ${KIND_TEXT[slot.kind].clock.toLowerCase()}`;
  return personName ? `${personName} · ${line}` : line;
}

// Nền tối riêng cho chú thích + thanh công cụ khi xem lớn: đọc được trên cả ảnh sáng lẫn chữ của
// trang phía sau (lớp phủ xem ảnh của antd khá trong).
// Gần đục để chữ của bảng phía sau không lộ qua thanh chú thích khi xem ảnh lớn.
const DOCK_BG = 'rgba(29, 29, 46, 0.94)';
const DOCK_HEIGHT = 44; // = chiều cao thanh công cụ mặc định của antd

const captionPill: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
  maxWidth: 'min(calc(100vw - 32px), 460px)',
  height: DOCK_HEIGHT,
  padding: '0 18px',
  borderRadius: 100,
  background: DOCK_BG,
  color: '#FFFFFF',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
};

const captionCounter: CSSProperties = {
  flex: 'none',
  paddingRight: 10,
  borderRight: '1px solid rgba(255, 255, 255, 0.28)',
  color: 'rgba(255, 255, 255, 0.72)',
  fontVariantNumeric: 'tabular-nums',
};

interface Props {
  record: AttendancePhotoSource;
  /** Tên nhân viên — hiện trên thanh chú thích khi xem ảnh lớn. */
  personName?: string;
  /** Cạnh ô ảnh (px). */
  size?: number;
}

const AttendancePhotos = ({ record, personName, size = 40 }: Props) => {
  const slots: Array<Omit<PhotoSlot, 'url'> & { url?: string | null }> = [
    { kind: 'in', label: 'Vào', url: record.checkinImageUrl, time: record.checkinTime },
    { kind: 'out', label: 'Ra', url: record.checkoutImageUrl, time: record.checkoutTime },
  ];
  const photos: PhotoSlot[] = slots
    .map((s) => ({ ...s, url: (s.url || '').trim() }))
    .filter((s) => !!s.url);

  return (
    // key: đổi bộ ảnh → dựng lại nhóm để thứ tự xem trước luôn là Vào rồi Ra.
    <Image.PreviewGroup
      key={photos.map((p) => p.url).join('|')}
      fallback={PHOTO_FALLBACK}
      preview={{
        // Bộ đếm "1 / 2" mặc định nằm trên thanh công cụ nên đè lên đáy ảnh lớn → ẩn đi,
        // đưa số thứ tự vào viên chú thích bên dưới.
        countRender: () => null,
        toolbarRender: (toolbar, { current, total, image }) => {
          // Theo vị trí (vào/ra có thể trùng URL); URL chỉ là phương án dự phòng.
          const shown = photos[current] || photos.find((p) => p.url === image?.url);
          const count = total ?? photos.length;
          const caption = shown ? captionOf(shown, personName) : '';
          return (
            // Chú thích và thanh công cụ nằm cùng một hàng (màn hẹp thì tự xuống hàng) để chân
            // trang thấp, không chạm vào ảnh. width: max-content vì khung chân trang của antd chỉ
            // rộng tối đa nửa màn hình.
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: 'max-content',
                maxWidth: 'calc(100vw - 32px)',
              }}
            >
              {shown && (
                <div style={captionPill} title={caption}>
                  {count > 1 && (
                    <span style={captionCounter}>
                      {current + 1}/{count}
                    </span>
                  )}
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {caption}
                  </span>
                </div>
              )}
              {cloneElement(toolbar, {
                style: { ...toolbar.props.style, height: DOCK_HEIGHT, background: DOCK_BG },
              })}
            </div>
          );
        },
      }}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        {slots.map((slot) => {
          const url = (slot.url || '').trim();
          return (
            <PhotoCell
              key={`${slot.kind}-${url}`}
              kind={slot.kind}
              label={slot.label}
              url={url}
              time={slot.time}
              size={size}
              personName={personName}
            />
          );
        })}
      </div>
    </Image.PreviewGroup>
  );
};

interface CellProps {
  kind: PhotoKind;
  label: string;
  url: string;
  time?: string | null;
  size: number;
  personName?: string;
}

const PhotoCell = ({ kind, label, url, time, size, personName }: CellProps) => {
  const [broken, setBroken] = useState(false);
  const clock = clockOf(time);

  // antd tự thay ảnh lỗi bằng `fallback` trước khi <img> kịp báo lỗi (nhất là với loading="lazy"),
  // nên tự dò URL để đổi chú thích thành "Không tải được ảnh". Trình duyệt dùng lại bộ nhớ đệm.
  useEffect(() => {
    if (!url) return;
    let alive = true;
    const probe = new window.Image();
    probe.onerror = () => {
      if (alive) setBroken(true);
    };
    probe.src = url;
    return () => {
      alive = false;
      probe.onerror = null;
    };
  }, [url]);
  const text = KIND_TEXT[kind];

  let tip: string;
  if (!url) tip = time ? 'Không có ảnh' : text.missingTime;
  else if (broken) tip = 'Không tải được ảnh';
  else
    tip = `${
      clock ? `${text.clock} ${clock}` : `Ảnh ${text.clock.toLowerCase()}`
    } · bấm để xem lớn`;

  const frame: CSSProperties = {
    width: size,
    height: size,
    borderRadius: 8,
    flex: 'none',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Tooltip title={tip}>
        {url ? (
          <span style={{ display: 'inline-flex', lineHeight: 0 }}>
            <Image
              src={url}
              alt={`Ảnh ${text.clock.toLowerCase()}${personName ? ` của ${personName}` : ''}`}
              width={size}
              height={size}
              loading="lazy"
              fallback={PHOTO_FALLBACK}
              preview={{ mask: <EyeOutlined style={{ fontSize: size >= 48 ? 18 : 14 }} /> }}
              style={{ objectFit: 'cover', borderRadius: 8 }}
              wrapperStyle={{
                ...frame,
                overflow: 'hidden',
                background: colors.surfaceMuted,
                boxShadow: `0 0 0 1px ${colors.line}`,
              }}
            />
          </span>
        ) : (
          <span
            role="img"
            aria-label={tip}
            style={{
              ...frame,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              border: `1px dashed ${colors.line}`,
              background: colors.surfaceMuted,
              color: colors.textMuted,
              fontSize: size >= 48 ? 20 : 16,
            }}
          >
            <UserOutlined aria-hidden />
          </span>
        )}
      </Tooltip>
      <span style={{ fontSize: 11, lineHeight: '14px', color: colors.textMuted }}>{label}</span>
    </div>
  );
};

export default AttendancePhotos;
